/**
 * Margin by month against the benchmark.
 *
 * One series, so no legend box -- the title names it. The benchmark is a
 * reference line, not a second series: it is a constant, and drawing it as a
 * series would invite reading it as data.
 */
import { useId, useState } from "react";
import {
  BENCHMARK,
  ChartFrame,
  EmptyChart,
  GRID,
  niceBounds,
  type Point,
  SERIES_1,
  useChartWidth,
} from "./chart-primitives";

const BASE_W = 720;
const H = 220;
const PAD = { top: 12, right: 16, bottom: 28, left: 44 };

export function MarginTrend({
  points,
  benchmark,
  onSelect,
  title = "Margin by month",
  subtitle,
}: {
  points: Point[];
  benchmark: number | null;
  onSelect?: (point: Point) => void;
  title?: string;
  subtitle?: string;
}) {
  const clipId = useId();
  const [hover, setHover] = useState<number | null>(null);
  const [frame, W] = useChartWidth(BASE_W);

  const plotted = points.filter((p) => p.value !== null);
  if (plotted.length === 0) {
    return (
      <ChartFrame title={title} subtitle={subtitle}>
        <EmptyChart message="No margin to show for this range" />
      </ChartFrame>
    );
  }

  const values = plotted.map((p) => p.value as number);

  // Clamp the axis to the bulk of the data. A single catastrophic month -- a
  // -200% margin on a few hundred dollars of revenue -- otherwise squashes
  // every other month into an unreadable band near the top, which is the
  // opposite of what a trend chart is for.
  //
  // Clipped points are NOT hidden: they are drawn at the edge as a hollow
  // marker and counted in the footer. Silently dropping them would be a chart
  // that lies; showing them off-scale is a chart that says "this one is off the
  // chart", which is the honest reading.
  // Tukey fences (quartiles +/- 1.5 x IQR), not raw percentiles. With a short
  // series -- a project with five closed months -- the 10th percentile IS the
  // outlier, so a percentile clamp silently did nothing and the axis still ran
  // to -400% with every real month flattened against the top.
  const sorted = [...values].sort((a, b) => a - b);
  const quartile = (q: number) => {
    const pos = (sorted.length - 1) * q;
    const base = Math.floor(pos);
    const next = sorted[base + 1];
    const cur = sorted[base] ?? 0;
    return next === undefined ? cur : cur + (next - cur) * (pos - base);
  };
  const q1 = quartile(0.25);
  const q3 = quartile(0.75);
  const iqr = q3 - q1;
  const pad = Math.max(10, iqr * 0.5);
  const clampLo = Math.min(q1 - 1.5 * iqr - pad, 0);
  const clampHi = q3 + 1.5 * iqr + pad;
  const inRange = values.filter((v) => v >= clampLo && v <= clampHi);
  const domainValues = inRange.length >= 2 ? inRange : values;
  const [lo, hi] = niceBounds(benchmark === null ? domainValues : [...domainValues, benchmark]);
  const clipped = plotted.filter((p) => (p.value as number) < lo || (p.value as number) > hi);
  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;

  const x = (i: number) =>
    PAD.left + (points.length === 1 ? innerW / 2 : (i / (points.length - 1)) * innerW);
  const y = (v: number) => PAD.top + innerH - ((v - lo) / (hi - lo)) * innerH;

  // Break the line at gaps rather than bridging them: a straight segment across
  // a month with no revenue would assert a trend that was never measured.
  const segments: { i: number; p: Point }[][] = [];
  let run: { i: number; p: Point }[] = [];
  points.forEach((p, i) => {
    if (p.value === null) {
      // A run of one is a lone point, not a line. Emitting a one-point polyline
      // puts a mark in the DOM that draws nothing -- the dot already shows it.
      if (run.length > 1) segments.push(run);
      run = [];
    } else {
      run.push({ i, p });
    }
  });
  if (run.length > 1) segments.push(run);

  const ticks = [lo, lo + (hi - lo) / 2, hi];
  const active = hover === null ? null : points[hover];

  return (
    <ChartFrame
      title={title}
      subtitle={subtitle}
      footer={
        // The off-scale note sits OUTSIDE the benchmark branch. Nested inside
        // it, a project page -- which has no benchmark -- drew its clipped
        // months as hollow markers with nothing to say what they were, which is
        // the silent-drop failure this note exists to prevent.
        <span className="flex flex-wrap items-center gap-x-4 gap-y-1">
          {benchmark !== null ? (
            <span className="flex items-center gap-1.5">
              <span
                aria-hidden
                className="inline-block h-px w-4"
                style={{ background: BENCHMARK }}
              />
              Benchmark {benchmark}%
            </span>
          ) : (
            <span>No benchmark is agreed at this level, so none is drawn.</span>
          )}
          {clipped.length > 0 ? (
            <span className="before:mr-3 before:text-border before:content-['·']">
              {clipped.length} month{clipped.length === 1 ? "" : "s"} beyond the axis (
              {clipped.map((c) => `${c.label}: ${(c.value as number).toFixed(0)}%`).join(", ")})
            </span>
          ) : null}
        </span>
      }
    >
      <div ref={frame} className="relative w-full overflow-x-auto">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="w-full"
          style={{ minWidth: 420 }}
          role="img"
          aria-label={`${title}. ${plotted.length} periods.`}
        >
          <title>{title}</title>
          <clipPath id={clipId}>
            <rect x={PAD.left} y={PAD.top} width={innerW} height={innerH} />
          </clipPath>

          {ticks.map((t) => (
            <g key={t}>
              <line
                x1={PAD.left}
                x2={W - PAD.right}
                y1={y(t)}
                y2={y(t)}
                stroke={GRID}
                strokeWidth={1}
              />
              <text
                x={PAD.left - 8}
                y={y(t) + 3}
                textAnchor="end"
                className="fill-muted-foreground"
                style={{ fontSize: 10 }}
              >
                {Math.round(t)}%
              </text>
            </g>
          ))}

          {benchmark !== null && benchmark >= lo && benchmark <= hi ? (
            <line
              x1={PAD.left}
              x2={W - PAD.right}
              y1={y(benchmark)}
              y2={y(benchmark)}
              stroke={BENCHMARK}
              strokeWidth={1.5}
              strokeDasharray="5 4"
            />
          ) : null}

          <g clipPath={`url(#${clipId})`}>
            {segments.map((seg) => (
              // Keyed on the segment's first point index, not on its position
              // in the array: a gap appearing earlier in the series shifts
              // every later segment's index and would remount all of them.
              <polyline
                key={`seg-${seg[0]?.i ?? "empty"}`}
                fill="none"
                stroke={SERIES_1}
                strokeWidth={2}
                strokeLinejoin="round"
                strokeLinecap="round"
                points={seg
                  .map((s) => `${x(s.i)},${y(Math.min(hi, Math.max(lo, s.p.value as number)))}`)
                  .join(" ")}
              />
            ))}
          </g>

          {points.map((p, i) => {
            if (p.value === null) return null;
            const v = p.value;
            const off = v < lo || v > hi;
            return (
              <circle
                key={p.label}
                cx={x(i)}
                cy={y(Math.min(hi, Math.max(lo, v)))}
                r={hover === i ? 5 : 3.5}
                // An off-scale point is hollow, so it reads as "beyond the axis"
                // rather than as an ordinary reading at the edge.
                fill={off ? "var(--background)" : SERIES_1}
                stroke={off ? SERIES_1 : "var(--background)"}
                strokeWidth={2}
              />
            );
          })}

          {points.map((p, i) => (
            <rect
              key={`hit-${p.label}`}
              x={x(i) - innerW / Math.max(points.length, 1) / 2}
              y={PAD.top}
              width={innerW / Math.max(points.length, 1)}
              height={innerH}
              fill="transparent"
              className={onSelect && p.value !== null ? "cursor-pointer" : undefined}
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
              onClick={() => (p.value === null ? undefined : onSelect?.(p))}
            />
          ))}

          {points.map((p, i) =>
            i % Math.ceil(points.length / 6) === 0 ? (
              <text
                key={`lbl-${p.label}`}
                x={x(i)}
                y={H - 8}
                textAnchor="middle"
                className="fill-muted-foreground"
                style={{ fontSize: 10 }}
              >
                {p.label.replace(/ \d{4}$/, "")}
              </text>
            ) : null,
          )}
        </svg>

        {active && active.value !== null ? (
          <div
            className="pointer-events-none absolute top-0 rounded-md border bg-popover px-2 py-1 text-xs shadow-sm"
            style={{ left: `${(x(hover as number) / W) * 100}%`, transform: "translateX(-50%)" }}
          >
            <div className="font-medium text-popover-foreground">{active.label}</div>
            <div className="tabular-nums text-muted-foreground">
              {(active.value as number).toFixed(1)}% margin
              {active.provisional ? " (forecast)" : ""}
            </div>
          </div>
        ) : null}
      </div>
    </ChartFrame>
  );
}
